import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Loader2 } from "lucide-react";

import { EmbeddedLoadingChart } from "@/components/ui/embedded-loading-chart";
import { Button } from "@/components/ui/button";
import { useActiveBrokerAccount } from "../../../../hooks/useActiveBrokerAccount";
import { useExecutionCandles } from "../../../../hooks/useTradeStatistics";
import { useInstrumentSetup } from "../../../../contexts/InstrumentSetupContext";
import { useWizardMarketDataOhlcGate } from "../../../../hooks/useWizardMarketDataOhlcGate";
import type { Trade } from "../../../../types/trade";
import { buildExecutionCandlesParams } from "../../../../utils/executionCandlesParams";
import { normalizeExecutionCandles } from "@/utils/executionCandlesNormalize";
import {
  enrichExecutionOverlayFromTrade,
  SL_COLOR,
  TP_COLOR,
  tradeToExecutionOverlay,
} from "../../../../utils/executionTradeOverlayLevels";
import { pickAssetClassFromTrades } from "../../../../utils/tradeAssetClass";
import { cn } from "@/lib/utils";
import { MarketDataUnavailableChartOverlay } from "@/components/MarketData/MarketDataUnavailableChartOverlay";
import { resolveTradeReviewChartUnavailablePayload } from "@/utils/marketDataUnavailable";
import ExecutionCandlesChart from "./TradeReview/ExecutionCandlesChart";
import { TradeReviewChartLegendArrow } from "./TradeReview/TradeReviewChartLegendArrow";

const CHART_HEIGHT = 360;

/**
 * Accept any RFC4122-ish UUID (v1–v8). Backend trade PKs are UUIDs —
 * non-uuid ids must not call trade_ids.
 */
function isTradeUuid(id: string | null | undefined): boolean {
  if (!id?.trim()) return false;
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
    id.trim()
  );
}

function toValidDate(value: Date | string | null | undefined): Date | null {
  if (value == null) return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Inline row chart: 30m for quick scalps (<3h), 1h otherwise. */
const INLINE_CHART_30M_MAX_HOLD_HOURS = 3;

function candleTypeForTrade(trade: Trade): string {
  const entry = toValidDate(trade.entryDate);
  if (!entry) return "1h";
  const exit = toValidDate(trade.exitDate) ?? new Date();
  const hours = Math.max((exit.getTime() - entry.getTime()) / (60 * 60 * 1000), 0);
  if (hours < INLINE_CHART_30M_MAX_HOLD_HOURS) return "30m";
  return "1h";
}

type TradeDetailsInlineTradeReviewProps = {
  trade: Trade;
  className?: string;
};

/**
 * Single-trade chart for the expanded Trade Log row.
 *
 * OHLC: timeframe=all-time + trade_ids so BE spans this trade (+ padding).
 * Do not pass focusTradeId into ExecutionCandlesChart — that dims every bar
 * outside [entry, exit] to ~28% opacity, which for short trades looks like a
 * single "open time" stick on an empty white chart.
 *
 * Overlay list is forced to this trade only (use Trade Review for multi-trade).
 */
export function TradeDetailsInlineTradeReview({
  trade,
  className,
}: TradeDetailsInlineTradeReviewProps) {
  const { activeAccountCode, activeBrokerName } = useActiveBrokerAccount();
  const { openSetup, hydratePendingForActiveAccount, isOpen: instrumentSetupOpen } =
    useInstrumentSetup();
  const [setupPending, setSetupPending] = useState<boolean | null>(null);
  const [setupChecking, setSetupChecking] = useState(false);

  const brokerAccountCode =
    (typeof trade.brokerAccountCode === "string" && trade.brokerAccountCode.trim()) ||
    activeAccountCode ||
    null;

  // Primitives only — row objects are new references on every table re-render.
  const tradeId = trade.id?.trim() ?? "";
  const symbol = trade.symbol?.trim() ?? "";
  const entryIso = toValidDate(trade.entryDate)?.toISOString() ?? "";
  const exitIso = toValidDate(trade.exitDate)?.toISOString() ?? "";
  const assetClassHint = trade.asset_class?.trim() || undefined;
  const tradeIdOk = isTradeUuid(tradeId);
  const brokerNameKey = activeBrokerName?.trim().toLowerCase() || "";

  useEffect(() => {
    if (!brokerAccountCode) {
      setSetupPending(false);
      setSetupChecking(false);
      return;
    }

    let cancelled = false;
    setSetupChecking(true);
    void hydratePendingForActiveAccount({ symbol: symbol || undefined }).then((pending) => {
      if (!cancelled) {
        setSetupPending(pending);
        setSetupChecking(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [brokerAccountCode, hydratePendingForActiveAccount, symbol]);

  const ohlcSetupGate = useWizardMarketDataOhlcGate({
    brokerAccountCode: brokerAccountCode ?? "",
    symbol: symbol || undefined,
    enabled: Boolean(brokerAccountCode) && tradeIdOk && setupPending === false && !setupChecking,
  });

  const openInstrumentSetup = useCallback(() => {
    if (!brokerAccountCode) return;
    void openSetup(brokerAccountCode, {
      mandatory: false,
      forceOpen: true,
      focusSymbol: symbol || undefined,
    });
  }, [brokerAccountCode, openSetup, symbol]);

  const handleChangeMarketDataSource = useCallback(() => {
    if (ohlcSetupGate.connectGate) {
      ohlcSetupGate.openMarketDataSetup();
      return;
    }
    openInstrumentSetup();
  }, [ohlcSetupGate, openInstrumentSetup]);

  const candleType = useMemo(
    () => candleTypeForTrade(trade),
    [entryIso, exitIso, tradeId]
  );

  const assetClass = useMemo(() => {
    return (
      assetClassHint ??
      pickAssetClassFromTrades([trade], tradeId) ??
      undefined
    );
  }, [assetClassHint, trade, tradeId]);

  const candlesEnabled =
    Boolean(brokerAccountCode) &&
    tradeIdOk &&
    setupPending === false &&
    !setupChecking &&
    !ohlcSetupGate.scanning &&
    !ohlcSetupGate.connectGate;

  const candlesParams = useMemo(() => {
    if (!candlesEnabled || !brokerAccountCode || !symbol || !tradeIdOk) return null;
    try {
      return buildExecutionCandlesParams({
        symbol,
        brokerAccountCode,
        brokerName: brokerNameKey || undefined,
        candleType,
        asset_class: assetClass,
        // BE derives OHLC window from this trade (+ default padding).
        requestWindow: {
          kind: "timeframe",
          timeframe: "all-time",
        },
        exactTradeIds: [tradeId],
        // No focus_trade_id — avoids BE marking most bars unrelated + FE dimming.
        cohortIntent: "policy_exact",
      });
    } catch (err) {
      console.error("[TradeDetailsInlineTradeReview] buildExecutionCandlesParams failed", err);
      return null;
    }
  }, [
    assetClass,
    brokerAccountCode,
    brokerNameKey,
    candleType,
    candlesEnabled,
    symbol,
    tradeId,
    tradeIdOk,
  ]);

  const handleMarketDataUnresolved = useCallback(
    (detail: { symbol?: string }) => {
      if (!brokerAccountCode) return;
      void openSetup(brokerAccountCode, {
        mandatory: false,
        forceOpen: true,
        focusSymbol: detail.symbol?.trim() || symbol || undefined,
      });
    },
    [brokerAccountCode, openSetup, symbol]
  );

  const {
    data,
    loading,
    backfillLoading,
    overlayRefreshing,
    error,
    errorAction,
    assetClassRequired,
    connectorRequired,
    marketDataUnavailable,
    refetch: refetchExecutionCandles,
  } = useExecutionCandles(candlesParams, {
    enabled: Boolean(candlesParams),
    onMarketDataUnresolved: handleMarketDataUnresolved,
  });

  const instrumentSetupWasOpenRef = useRef(false);
  useEffect(() => {
    if (instrumentSetupWasOpenRef.current && !instrumentSetupOpen) {
      void hydratePendingForActiveAccount({ symbol: symbol || undefined }).then((pending) => {
        setSetupPending(pending);
      });
      refetchExecutionCandles();
    }
    instrumentSetupWasOpenRef.current = instrumentSetupOpen;
  }, [hydratePendingForActiveAccount, instrumentSetupOpen, refetchExecutionCandles, symbol]);

  useEffect(() => {
    const handleTradesUpdated = (event: Event) => {
      const detail = (event as CustomEvent<{ action?: string; accountCode?: string }>).detail;
      if (detail?.action !== "marketDataPreference") return;
      const code = brokerAccountCode?.trim();
      if (detail.accountCode && code && detail.accountCode !== code) return;
      void hydratePendingForActiveAccount({ symbol: symbol || undefined }).then((pending) => {
        setSetupPending(pending);
      });
      refetchExecutionCandles();
    };
    window.addEventListener("tradesUpdated", handleTradesUpdated);
    return () => window.removeEventListener("tradesUpdated", handleTradesUpdated);
  }, [brokerAccountCode, hydratePendingForActiveAccount, refetchExecutionCandles, symbol]);

  // Keep partial OHLC while backfill continues (same as Trade Review).
  const candles = useMemo(
    () => normalizeExecutionCandles(data?.candles ?? []),
    [data?.candles]
  );
  const hasCandles = candles.some((c) => Boolean(c.time));

  // Only while a request is actually in flight — never treat idle empty as "loading".
  const chartDataPending =
    Boolean(candlesParams) &&
    !hasCandles &&
    (loading || overlayRefreshing || backfillLoading);

  const effectiveSymbol = data?.resolved_symbol || symbol;

  // This trade only — never paint the full symbol cohort.
  const tradesForChart = useMemo(() => {
    const fallback = tradeToExecutionOverlay(trade);
    const fromApi = data?.trades ?? [];
    if (!fromApi.length) return [fallback];

    const match =
      fromApi.find((row) => row.trade_id === tradeId) ??
      fromApi.find(
        (row) => String(row.trade_id).toLowerCase() === tradeId.toLowerCase()
      );

    if (!match) return [fallback];
    return [enrichExecutionOverlayFromTrade(match, trade)];
  }, [data?.trades, trade, tradeId]);

  const chartUnavailablePayload = useMemo(
    () =>
      resolveTradeReviewChartUnavailablePayload({
        marketDataUnavailable,
        proactiveConnectGate: ohlcSetupGate.connectGate,
        connectorRequired,
      }),
    [connectorRequired, marketDataUnavailable, ohlcSetupGate.connectGate]
  );

  const chartShellPending =
    ohlcSetupGate.scanning ||
    (Boolean(candlesParams) && chartDataPending && !chartUnavailablePayload);

  const showBuildParamsError =
    !candlesParams && !chartUnavailablePayload && !chartShellPending;

  const chartOhlcBlocked = Boolean(chartUnavailablePayload);
  const canRenderChart = hasCandles && !assetClassRequired && !chartOhlcBlocked;

  const showLoadingOverlay = chartShellPending;

  // While backfill is still filling bars, dim overlay note only (chart already visible).
  const isBackfillingMore =
    canRenderChart && (backfillLoading || overlayRefreshing) && candles.length > 0;

  if (!brokerAccountCode) {
    return (
      <div data-trade-details-inline-review="" className={cn("min-w-0 w-full", className)}>
        <HeaderCopy />
        <StatusMessage>
          Connect a broker account to plot this trade on the chart.
        </StatusMessage>
      </div>
    );
  }

  if (!tradeIdOk) {
    return (
      <div data-trade-details-inline-review="" className={cn("min-w-0 w-full", className)}>
        <HeaderCopy />
        <StatusMessage>
          This trade has no chart id (expected a UUID). Re-sync or re-log the trade to enable review.
        </StatusMessage>
      </div>
    );
  }

  if (setupChecking) {
    return (
      <div data-trade-details-inline-review="" className={cn("min-w-0 w-full", className)}>
        <HeaderCopy />
        <div
          className="relative flex min-h-[360px] w-full items-center justify-center overflow-hidden rounded-[6px] border border-border"
        >
          <EmbeddedLoadingChart />
        </div>
        <p className="mt-2 flex items-center justify-center gap-2 font-[family-name:var(--font-primary)] text-sm font-medium text-[color:var(--brand-tropical-surf)]">
          <Loader2 className="size-3.5 animate-spin" aria-hidden />
          Checking instrument setup…
        </p>
      </div>
    );
  }

  if (setupPending) {
    return (
      <div data-trade-details-inline-review="" className={cn("min-w-0 w-full", className)}>
        <HeaderCopy />
        <div className="flex min-h-[200px] flex-col items-center justify-center gap-4 rounded-[6px] border border-border px-6 py-10 text-center">
          <StatusMessage className="max-w-md">
            Confirm your instruments and preferred asset classes for this account before Tessera can
            load price charts for{" "}
            <span className="font-semibold text-[color:var(--brand-tropical-surf)]">
              {symbol || "this trade"}
            </span>
            .
          </StatusMessage>
          <Button
            type="button"
            variant="gradient"
            size="sm"
            data-font="primary"
            onClick={openInstrumentSetup}
          >
            Configure instruments
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div
      data-trade-details-inline-review=""
      className={cn("min-w-0 w-full", className)}
    >
      <HeaderCopy />

      <div
        className="relative min-h-[360px] w-full min-w-0 overflow-hidden rounded-[6px] border border-border"
        style={{ width: "100%" }}
      >
        {canRenderChart ? (
          <ExecutionCandlesChart
            // Remount when bars grow (backfill) so fitContent can re-run on fuller data.
            key={`${tradeId}-${effectiveSymbol}-${candles.length > 10 ? "full" : candles.length}`}
            candles={candles}
            trades={tradesForChart}
            selectedTradeId={tradeId}
            // Never focus-dim here: inline view is already this trade only.
            height={CHART_HEIGHT}
            instrumentLabel={effectiveSymbol}
            showNavigationControls
            showPnlBackgroundOverlay
          />
        ) : null}

        {showLoadingOverlay ? (
          <div className="absolute inset-0 z-[1]">
            <EmbeddedLoadingChart />
          </div>
        ) : null}

        {!chartShellPending && assetClassRequired ? (
          <div className="absolute inset-0 z-[1] flex flex-col items-center justify-center gap-4 bg-background/90 p-6">
            <StatusMessage className="max-w-md text-center">
              Market data needs an asset class for{" "}
              <span className="font-semibold text-[color:var(--brand-tropical-surf)]">
                {symbol}
              </span>
              . Configure instruments for this account, then reopen this row.
            </StatusMessage>
            <Button
              type="button"
              variant="gradient"
              size="sm"
              data-font="primary"
              onClick={openInstrumentSetup}
            >
              Configure instruments
            </Button>
          </div>
        ) : null}

        {!chartShellPending && !assetClassRequired && chartUnavailablePayload ? (
          <MarketDataUnavailableChartOverlay
            payload={chartUnavailablePayload}
            onChangeDataSource={handleChangeMarketDataSource}
          />
        ) : null}

        {!chartShellPending && showBuildParamsError ? (
          <div className="absolute inset-0 z-[1] flex items-center justify-center bg-background/90 p-6">
            <StatusMessage className="max-w-md text-center">
              Could not build the chart request for this trade. Missing symbol or date window.
            </StatusMessage>
          </div>
        ) : null}

        {!chartShellPending && !assetClassRequired && error && !hasCandles && !chartUnavailablePayload && !showBuildParamsError ? (
          <div className="absolute inset-0 z-[1] flex items-center justify-center bg-background/90 p-6">
            <StatusMessage className="max-w-md text-center">
              {error}
              {errorAction ? ` ${errorAction}` : ""}
            </StatusMessage>
          </div>
        ) : null}

        {!chartShellPending &&
        !assetClassRequired &&
        !error &&
        !hasCandles &&
        !chartUnavailablePayload &&
        !showBuildParamsError ? (
          <div className="absolute inset-0 z-[1] flex items-center justify-center bg-background/90 p-6">
            <StatusMessage className="max-w-md text-center">
              No price candles returned for {symbol}. Try Trade Review for this symbol, or configure
              instruments if market data is not set up yet.
            </StatusMessage>
          </div>
        ) : null}
      </div>

      <div
        className="mt-2 flex min-h-[42px] items-center justify-center rounded-[6px] border border-border px-3 py-2.5"
        data-trade-details-inline-review-legend=""
      >
        {showLoadingOverlay ? (
          <p className="flex items-center gap-2 font-[family-name:var(--font-primary)] text-sm font-medium text-[color:var(--brand-tropical-surf)]">
            <Loader2 className="size-3.5 animate-spin" aria-hidden />
            Loading chart for {symbol}…
          </p>
        ) : chartUnavailablePayload ? (
          <p className="m-0 text-center font-[family-name:var(--font-primary)] text-sm font-normal text-gray-500 dark:text-sidebar-muted-foreground">
            Chart unavailable for {symbol || "this trade"}.
          </p>
        ) : canRenderChart ? (
          <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2 font-['IBM_Plex_Sans',sans-serif] text-xs text-gray-500 dark:text-sidebar-muted-foreground">
            <span className="inline-flex items-center gap-1.5">
              <span className="inline-block size-2 rounded-full bg-[#06E363]" />
              Long entry
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="inline-block size-2 rounded-full bg-[#E00101]" />
              Short entry
            </span>
            <span className="inline-flex items-center gap-1.5">
              <TradeReviewChartLegendArrow direction="long" className="shrink-0" />
              <span className="font-[family-name:var(--font-primary)] text-xs font-medium leading-none text-[#06E363]">
                Long Win
              </span>
            </span>
            <span className="inline-flex items-center gap-1.5">
              <TradeReviewChartLegendArrow direction="long" className="shrink-0" />
              <span className="font-[family-name:var(--font-primary)] text-xs font-medium leading-none text-[#E00101]">
                Long Loss
              </span>
            </span>
            <span className="inline-flex items-center gap-1.5">
              <TradeReviewChartLegendArrow direction="short" className="shrink-0" />
              <span className="font-[family-name:var(--font-primary)] text-xs font-medium leading-none text-[#06E363]">
                Short Win
              </span>
            </span>
            <span className="inline-flex items-center gap-1.5">
              <TradeReviewChartLegendArrow direction="short" className="shrink-0" />
              <span className="font-[family-name:var(--font-primary)] text-xs font-medium leading-none text-[#E00101]">
                Short Loss
              </span>
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="inline-block h-0.5 w-5 rounded-sm" style={{ backgroundColor: SL_COLOR }} />
              Stop loss
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="inline-block h-0.5 w-5 rounded-sm" style={{ backgroundColor: TP_COLOR }} />
              Take profit
            </span>
          </div>
        ) : isBackfillingMore ? (
          <p className="flex items-center gap-2 font-[family-name:var(--font-primary)] text-sm font-medium text-[color:var(--brand-tropical-surf)]">
            <Loader2 className="size-3.5 animate-spin" aria-hidden />
            Filling price history…
          </p>
        ) : showBuildParamsError ? (
          <StatusMessage>Chart unavailable for this trade.</StatusMessage>
        ) : chartUnavailablePayload ? null : (
          <StatusMessage>Chart unavailable for this trade.</StatusMessage>
        )}
      </div>
    </div>
  );
}

function HeaderCopy() {
  return (
    <div className="mb-2 flex flex-col gap-1">
      <p className="font-[family-name:var(--font-primary)] text-sm font-normal text-[color:var(--brand-tropical-surf)]">
        Trade review
      </p>
      <p className="font-[family-name:var(--font-primary)] text-sm font-normal text-gray-500 dark:text-sidebar-muted-foreground">
        Price action for this trade only — loaded when you open the row.
      </p>
    </div>
  );
}

function StatusMessage({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <p
      className={cn(
        "font-[family-name:var(--font-primary)] text-sm font-normal text-gray-500 dark:text-sidebar-muted-foreground",
        className
      )}
    >
      {children}
    </p>
  );
}

export default TradeDetailsInlineTradeReview;
